/**
 * Qui décide de l'apparence NATIVE, et quand — la règle, seule et testable.
 *
 * Elle existe parce que le sombre a deux étages. Le thème du design system (`parentThemeFor`) ne
 * peint que ce que React rend ; l'apparence native, elle, décide de ce que le SYSTÈME dessine —
 * le clavier, les alertes, les sélecteurs de date. Tant que l'`Info.plist` d'iOS portait
 * `UIUserInterfaceStyle = Light`, les deux étages étaient d'accord par accident : rien ne pouvait
 * passer en sombre. En le passant à `Automatic` (`app.json`), l'app suivrait le système PARTOUT,
 * et un clavier nuit se serait ouvert sur les écrans blancs de l'élève et du legacy.
 *
 * D'où la règle : sur iOS l'app repart du clair au démarrage — le comportement d'aujourd'hui,
 * rendu explicite — et seul l'espace parent (comme l'espace élève) pose sa propre apparence le
 * temps qu'il est monté : celle que le parent a choisie dans la feuille « Changer de profil ».
 * Automatique rend la main au système, Clair et Sombre la forcent — sans quoi le clavier et les
 * alertes resteraient clairs sur une page nuit, ou l'inverse. Sur Android on ne pose rien :
 * `useColorScheme()` y lit déjà le système et `userInterfaceStyle` y est sans effet sans
 * `expo-system-ui`, que l'app n'installe pas ; le thème du design system, lui, suit quand même
 * la préférence.
 */
import { Appearance } from "react-native"

import type { ParentAppearancePreference } from "./parentAppearancePreference"

/** Ce qu'il y a à faire de l'apparence native. `leave-alone` : ne rien poser du tout. */
export type AppearanceMove = "force-light" | "force-dark" | "follow-system" | "leave-alone"

/** Les trois moments où la question se pose. */
export type AppearanceMoment = "start" | "enter" | "exit"

/**
 * `platform` est `Platform.OS`. Le clair du DÉMARRAGE n'est pas un choix de thème, c'est la
 * remise à zéro de ce que le plist tenait jusqu'ici. `preference` ne pèse que sur l'entrée ; absente
 * (le démarrage), elle vaut Automatique.
 *
 * `enter` vaut aussi pour un changement de préférence pendant qu'on est dans l'espace parent : la
 * question est la même — qu'est-ce que l'espace parent pose en ce moment ? La réponse ne dépend
 * jamais de l'apparence lue (`useColorScheme()`), qui dit ce qu'on a forcé tant qu'on force :
 * c'est ce qui garantit qu'un retour à Automatique rend vraiment la main au système.
 */
export function parentAppearanceAt(
    moment: AppearanceMoment,
    platform: string,
    preference: ParentAppearancePreference = "auto"
): AppearanceMove {
    if (platform !== "ios") return "leave-alone"
    if (moment === "start") return "force-light"
    if (moment === "exit") return "force-light"
    if (preference === "light") return "force-light"
    return preference === "dark" ? "force-dark" : "follow-system"
}

/**
 * Pose la décision. `null` est le mot d'`Appearance` pour « plus d'apparence forcée, suis le
 * système » ; ne rien appeler du tout et poser `"light"` ne sont pas la même chose, d'où les
 * trois cas plutôt qu'une couleur nullable.
 */
export function applyAppearanceMove(move: AppearanceMove): void {
    if (move === "leave-alone") return
    Appearance.setColorScheme(move === "force-light" ? "light" : move === "force-dark" ? "dark" : null)
}
